import { useMemo } from 'react'

import {
  BrokenImageOutlined,
  CheckCircleOutlineOutlined,
  GppGoodOutlined,
  GppMaybeOutlined,
  ReportProblemOutlined,
  SupportAgent,
  VideocamOffOutlined,
  WifiOffOutlined,
} from '@mui/icons-material'
import { Stack, Tooltip, Typography } from '@mui/material'
import { AlertSeverityEnum, SmepEvent, SmepEventHealthEnum, SmepEventStateEnum } from '@nativewaves/nw-cloud-sdk/smep'
import { ColumnDef } from '@tanstack/react-table'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { CopyId } from '@shared/components/Table/Fields'
import { DATE_TIME_FORMAT } from '@shared/utils/support'
import { IconButtonLink } from 'components/LinkedMUIComponents'
import { MediaSummary, NoSrcPlaceholderWrapper, StatusCell, TagSummary, Thumbnail } from 'domains/Workspaces/Events'
import { readFixtureId } from 'domains/Workspaces/Events/Event/ConfigEditor/model'
import { findOnCallPosition } from 'domains/Workspaces/Events/Event/onCall'
import { SortableHeader } from 'domains/Workspaces/Events/SortableHeader'
import { buildCameraSummary } from 'domains/Workspaces/Events/useCameraStreamNames'
import { useUserInfo } from 'hooks/useUserInfo'
import { AlertBadge } from 'layouts'
import { eventsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Events'
import { eventMonitoringRoute } from 'pages/NWPlatform/Workspaces/Workspace/Events/Event'
import { getAlertSeverityColor } from 'utils'

export type UseColumnsProps = {
  /** Resolved camera stream names, by stream id — see `useCameraStreamNames`. */
  cameraStreamNames: Map<string, string>
}

export const useColumns = ({ cameraStreamNames }: UseColumnsProps) => {
  const { t } = useTranslation(['common', 'domain'])
  // Shared, long-lived query — the navigation already holds it, so this costs no extra request.
  const userInfoQuery = useUserInfo()
  const currentUserId = userInfoQuery.data?.sub

  return useMemo<ColumnDef<SmepEvent>[]>(
    () => [
      {
        accessorFn: (row) => ({ src: row.thumbnail?.url, alt: row.name }),
        id: '_thumbnail',
        header: () => null,
        meta: {
          sizing: {
            width: 96,
            px: 2,
          },
          align: 'center',
        },
        cell: ({ getValue }) => {
          const { src, alt } = getValue<{ src?: string; alt: string }>()
          if (src) {
            return (
              <picture>
                <Thumbnail type="image/jpeg" data={src} aria-label={alt}>
                  <NoSrcPlaceholderWrapper>
                    <BrokenImageOutlined />
                  </NoSrcPlaceholderWrapper>
                </Thumbnail>
              </picture>
            )
          }
          return (
            <NoSrcPlaceholderWrapper>
              <VideocamOffOutlined />
            </NoSrcPlaceholderWrapper>
          )
        },
      },
      {
        accessorFn: (row) => row.name ?? t('noNameProvided'),
        id: '_name',
        header: () => <SortableHeader title={t('name')} sortProp="orderByName" />,
        meta: {
          sizing: {
            minWidth: 220,
          },
        },
        cell: ({ getValue }) => (
          <Typography variant="inherit" noWrap>
            {getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorFn: (row) => ({ state: row.state, health: row.health, startTime: row.startTime }),
        id: '_status',
        header: () => <SortableHeader title={t('domain:Workspace.Event.status')} sortProp="orderByState" />,
        meta: {
          sizing: {
            width: 130,
            px: 2,
          },
          align: 'center',
        },
        cell: (cell) => {
          const { state, health, startTime } = cell.getValue<{
            state: SmepEventStateEnum
            health: SmepEventHealthEnum
            startTime: string
          }>()

          return <StatusCell state={state} health={health} startTime={startTime} />
        },
      },
      {
        accessorFn: (row) => ({ verified: row.verified, verifiedTime: row.verifiedTime }),
        id: '_verified',
        header: t('domain:Workspace.Event.Verification.columnHeader'),
        meta: {
          sizing: {
            width: 90,
            px: 2,
          },
          align: 'center',
        },
        cell: (cell) => {
          // Only the flag and the timestamp survive the list endpoint — the verifier's name is
          // resolved on the event page, where a single lookup is worth it.
          const { verified, verifiedTime } = cell.getValue<{ verified?: boolean; verifiedTime?: string | null }>()

          return (
            <Tooltip
              title={
                verified
                  ? verifiedTime
                    ? t('domain:Workspace.Event.Verification.verifiedOn', {
                        date: format(new Date(verifiedTime), DATE_TIME_FORMAT),
                      })
                    : t('domain:Workspace.Event.Verification.verifiedLabel')
                  : t('domain:Workspace.Event.Verification.notVerified')
              }
            >
              {verified ? (
                <GppGoodOutlined sx={{ color: (theme) => theme.palette.success[theme.palette.mode] }} />
              ) : (
                <GppMaybeOutlined sx={{ color: 'text.disabled' }} />
              )}
            </Tooltip>
          )
        },
      },
      {
        accessorKey: 'alerts',
        header: t('domain:Workspace.Event.alerts'),
        meta: {
          sizing: {
            width: 80,
            px: 2,
          },
          align: 'center',
        },
        cell: (cell) => {
          const alerts = cell.getValue<SmepEvent['alerts']>()

          if (alerts) {
            const severityLevel = alerts.some((item) => item.severity === AlertSeverityEnum.High)
              ? AlertSeverityEnum.High
              : AlertSeverityEnum.Medium
            return alerts.length > 0 ? (
              <AlertBadge
                badgeContent={alerts.length}
                color={severityLevel === AlertSeverityEnum.High ? 'error' : 'warning'}
                overlap="circular"
              >
                <IconButtonLink
                  size="small"
                  from={eventsOverviewRoute.fullPath}
                  to={eventMonitoringRoute.to}
                  params={{ eventId: cell.row.original.id }}
                >
                  <ReportProblemOutlined
                    sx={{
                      color: (theme) => getAlertSeverityColor(severityLevel, theme.palette.mode),
                    }}
                  />
                </IconButtonLink>
              </AlertBadge>
            ) : (
              <CheckCircleOutlineOutlined
                sx={{
                  color: (theme) => theme.palette.success[theme.palette.mode],
                }}
              />
            )
          } else {
            return (
              <Tooltip title={t('domain:Workspace.Event.eventAlertsNotLoaded')}>
                <WifiOffOutlined
                  sx={{
                    color: (theme) => theme.palette.info[theme.palette.mode],
                  }}
                />
              </Tooltip>
            )
          }
        },
      },
      {
        accessorFn: (row) => row.onCall,
        id: '_onCall',
        header: t('domain:Workspace.Event.OnCall.title'),
        meta: {
          sizing: {
            width: 90,
            px: 2,
          },
          align: 'center',
        },
        cell: (cell) => {
          // Names stay unresolved outside `GET /events/{eventId}`, so the count is all this column
          // can honestly show. An absent list (older payloads) is not the same as nobody on call.
          const onCall = cell.getValue<SmepEvent['onCall'] | undefined>()

          if (!onCall) {
            return null
          }

          if (!onCall.length) {
            return (
              <Tooltip title={t('domain:Workspace.Event.OnCall.nobodyOnCall')}>
                <ReportProblemOutlined
                  sx={{
                    color: (theme) => theme.palette.warning[theme.palette.mode],
                  }}
                />
              </Tooltip>
            )
          }

          // Being on call yourself is the one thing in this column worth acting on, so it gets the
          // accent colour and a tooltip that names where in the escalation order you sit.
          const position = findOnCallPosition(onCall, currentUserId)

          return (
            <Tooltip
              title={
                position
                  ? t('domain:Workspace.Event.OnCall.youAreOnCall', { position, total: onCall.length })
                  : t('domain:Workspace.Event.OnCall.peopleOnCall', { count: onCall.length })
              }
            >
              <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                <SupportAgent fontSize="small" sx={{ color: position ? 'primary.main' : 'text.secondary' }} />
                <Typography
                  variant="text-sm"
                  sx={position ? { color: 'primary.main', fontWeight: 'fontWeightMedium' } : undefined}
                >
                  {onCall.length}
                </Typography>
              </Stack>
            </Tooltip>
          )
        },
      },
      {
        accessorKey: 'startTime',
        header: () => <SortableHeader title={t('domain:Workspace.Event.startTime')} sortProp="orderByStartTime" />,
        meta: {
          sizing: {
            minWidth: 150,
            px: 1,
          },
        },
        cell: ({ getValue }) => format(new Date(getValue<string>()), DATE_TIME_FORMAT),
      },
      {
        accessorKey: 'endTime',
        header: () => <SortableHeader title={t('domain:Workspace.Event.endTime')} sortProp="orderByEndTime" />,
        meta: {
          sizing: {
            minWidth: 150,
          },
        },
        cell: ({ getValue }) =>
          getValue<string>() ? format(new Date(getValue<string>()), DATE_TIME_FORMAT) : 'Open end',
      },
      {
        // The stream is what identifies an angle here — camera names are often left empty.
        id: '_cameras',
        header: t('domain:Workspace.Event.ConfigEditor.cameras'),
        meta: {
          sizing: {
            minWidth: 160,
            px: 1,
          },
        },
        cell: ({ row }) => <MediaSummary kind="video" items={buildCameraSummary(row.original, cameraStreamNames)} />,
      },
      {
        accessorFn: (row) => Array.from(row.config?.tags ?? []),
        id: '_tags',
        header: t('domain:Workspace.Event.tags'),
        meta: {
          sizing: {
            minWidth: 160,
            px: 1,
          },
        },
        cell: ({ getValue }) => <TagSummary tags={getValue<string[]>()} />,
      },
      {
        accessorFn: (row) => (row.config ? readFixtureId(row.config) : ''),
        id: '_fixtureId',
        header: t('domain:Workspace.Event.fixtureId'),
        meta: {
          sizing: {
            minWidth: 130,
            px: 1,
          },
        },
        cell: ({ getValue }) => (
          <Typography variant="inherit" noWrap>
            {getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'id',
        header: t('id'),
        meta: {
          sizing: {
            width: 170,
            px: 2,
          },
        },
        cell: ({ getValue }) => <CopyId id={getValue<string>()} />,
      },
    ],
    [cameraStreamNames, currentUserId, t],
  )
}
